import { Controller } from "@hotwired/stimulus";

// Connects with data-controller="platform-notification-banner"
//
// Provides dismiss and expand/collapse functionality for platform notification banners.
// Dismiss state is stored in localStorage, keyed on a unique dismiss key that changes
// when a new notification event occurs (so dismissed banners reappear for new events).
export default class extends Controller {
  static values = { dismissKey: String };
  static targets = ["details", "expandButton"];

  declare dismissKeyValue: string;
  declare detailsTarget: HTMLElement;
  declare expandButtonTarget: HTMLElement;
  declare hasDetailsTarget: boolean;
  declare hasExpandButtonTarget: boolean;

  connect() {
    if (this.isDismissed()) {
      this.element.remove();
    }
  }

  dismiss() {
    this.cleanupStaleDismissals();
    localStorage.setItem(this.dismissKeyValue, "true");
    this.element.remove();
  }

  toggleDetails() {
    if (!this.hasDetailsTarget) {
      return;
    }

    const isHidden = this.detailsTarget.classList.contains("hidden");

    if (isHidden) {
      this.detailsTarget.classList.remove("hidden");
      if (this.hasExpandButtonTarget) {
        this.expandButtonTarget.textContent = "Less ▴";
        this.expandButtonTarget.setAttribute("aria-expanded", "true");
      }
    } else {
      this.detailsTarget.classList.add("hidden");
      if (this.hasExpandButtonTarget) {
        this.expandButtonTarget.textContent = "Details ▾";
        this.expandButtonTarget.setAttribute("aria-expanded", "false");
      }
    }
  }

  private isDismissed(): boolean {
    return localStorage.getItem(this.dismissKeyValue) === "true";
  }

  // Remove stale dismiss keys from previous rate limit events for the same
  // org, keeping the current one. Only cleans up keys matching this org's
  // prefix so that dismissals in other orgs are preserved.
  private cleanupStaleDismissals() {
    // dismissKeyValue is "github_rate_limit:{org_id}:{reset_at}"
    // Strip the last segment to get the org-scoped prefix
    const orgPrefix = this.dismissKeyValue.substring(0, this.dismissKeyValue.lastIndexOf(":") + 1);

    for (let idx = localStorage.length - 1; idx >= 0; idx--) {
      const key = localStorage.key(idx);
      if (key && key.startsWith(orgPrefix) && key !== this.dismissKeyValue) {
        localStorage.removeItem(key);
      }
    }
  }
}
